import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * ImageDebugPanel - Visual debugging for multimodal image selection
 *
 * Shows selected and rejected images from Gemini multimodal summarization.
 * Only displayed in development environment.
 *
 * Supports both new multimodal format (dict) and legacy CLIP format (array).
 */
const ImageDebugPanel = ({ debugData }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (import.meta.env.MODE === 'production') return null;
  if (!debugData) return null;

  // New multimodal format: { type: "multimodal", selected_images: [...], rejected_images: [...] }
  if (debugData.type === 'multimodal') {
    return <MultimodalPanel data={debugData} isExpanded={isExpanded} setIsExpanded={setIsExpanded} />;
  }

  // Legacy CLIP format: array of image objects
  if (Array.isArray(debugData) && debugData.length > 0) {
    return <LegacyClipPanel debugData={debugData} isExpanded={isExpanded} setIsExpanded={setIsExpanded} />;
  }

  return null;
};

/** New multimodal debug panel */
const MultimodalPanel = ({ data, isExpanded, setIsExpanded }) => {
  const selected = data.selected_images || [];
  const rejected = data.rejected_images || [];
  const total = data.images_sent || (selected.length + rejected.length);

  return (
    <div className="mt-8 p-4 border-2 border-purple-500 rounded-clay bg-purple-50 dark:bg-purple-950/30">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-bold text-purple-900 dark:text-purple-200">
            🔬 Multimodal Debug (Dev Only)
          </h3>
          <div className="flex gap-2 text-sm flex-wrap">
            <span className="px-3 py-1 bg-surface rounded border border-purple-300">
              Model: <strong>{data.model}</strong>
            </span>
            <span className="px-3 py-1 bg-surface rounded border border-purple-300">
              Res: <strong>{data.resolution}</strong>
            </span>
            <span className="px-3 py-1 bg-surface rounded border border-purple-300">
              Sent: <strong>{total}</strong>
            </span>
            <span className="px-3 py-1 bg-green-100 dark:bg-green-900/40 rounded border border-green-300 dark:border-green-800">
              Selected: <strong>{selected.length}</strong>
            </span>
            <span className="px-3 py-1 bg-red-100 dark:bg-red-900/40 rounded border border-red-300 dark:border-red-800">
              Rejected: <strong>{rejected.length}</strong>
            </span>
            <span className="px-3 py-1 bg-surface rounded border border-purple-300">
              {data.latency_s}s
            </span>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
        >
          {isExpanded ? 'Hide Details' : 'Show Details'}
        </button>
      </div>

      {isExpanded && (
        <div className="space-y-4">
          {/* Selected images */}
          {selected.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-green-700 dark:text-green-300 mb-2">
                Selected Images ({selected.length})
              </h4>
              <div className="grid grid-cols-1 gap-2">
                {selected.map((img, idx) => (
                  <div key={idx} className="flex gap-3 p-2 bg-green-50 dark:bg-green-950/30 rounded border border-green-200 dark:border-green-800">
                    {img.url && (
                      <img
                        src={img.url}
                        alt={img.reason || 'Selected image'}
                        className="w-48 h-16 object-cover rounded border border-theme-border-subtle flex-shrink-0"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-green-600 text-white">
                          #{img.index}
                        </span>
                        <span className="text-xs text-green-700 dark:text-green-300">{img.reason}</span>
                      </div>
                      {img.ocr_text && (
                        <div className="text-xs text-secondary-text mt-1 font-mono truncate">
                          OCR: {img.ocr_text}
                        </div>
                      )}
                      {img.url && (
                        <a href={img.url} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline font-mono truncate block mt-1">
                          {img.url}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rejected images */}
          {rejected.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-red-700 dark:text-red-300 mb-2">
                Rejected Images ({rejected.length})
              </h4>
              <div className="grid grid-cols-1 gap-2">
                {rejected.map((img, idx) => (
                  <div key={idx} className="flex gap-3 p-2 bg-red-50 dark:bg-red-950/30 rounded border border-red-200 dark:border-red-800">
                    {img.url && (
                      <img
                        src={img.url}
                        alt={img.reason || 'Rejected image'}
                        className="w-48 h-16 object-cover rounded border border-theme-border-subtle flex-shrink-0 opacity-60"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-red-600 text-white">
                          #{img.index}
                        </span>
                        <span className="text-xs text-red-700 dark:text-red-300">{img.reason}</span>
                      </div>
                      {img.url && (
                        <a href={img.url} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline font-mono truncate block mt-1">
                          {img.url}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Legacy CLIP debug panel (backward compat for old articles) */
const LegacyClipPanel = ({ debugData, isExpanded, setIsExpanded }) => {
  const totalImages = debugData.length;
  const selectedImages = debugData.filter(img => img.passed_threshold).length;

  return (
    <div className="mt-8 p-4 border-2 border-blue-500 rounded-clay bg-blue-50 dark:bg-blue-950/30">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-bold text-blue-900 dark:text-blue-200">
            🔬 CLIP Debug (Legacy)
          </h3>
          <div className="flex gap-2 text-sm">
            <span className="px-3 py-1 bg-surface rounded border border-blue-300">
              Total: <strong>{totalImages}</strong>
            </span>
            <span className="px-3 py-1 bg-green-100 dark:bg-green-900/40 rounded border border-green-300 dark:border-green-800">
              Selected: <strong>{selectedImages}</strong>
            </span>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          {isExpanded ? 'Hide Details' : 'Show Details'}
        </button>
      </div>

      {isExpanded && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-blue-100 dark:bg-blue-900/40 border-b-2 border-blue-300 dark:border-blue-800">
                <th className="p-2 text-left font-semibold">Preview</th>
                <th className="p-2 text-center font-semibold">CLIP Score</th>
                <th className="p-2 text-center font-semibold">Status</th>
                <th className="p-2 text-left font-semibold">Info</th>
              </tr>
            </thead>
            <tbody>
              {debugData.map((img, idx) => (
                <tr key={idx} className={`border-b border-theme-border-subtle ${
                  img.passed_threshold
                    ? 'bg-green-50 dark:bg-green-950/30'
                    : 'bg-red-50 dark:bg-red-950/30'
                }`}>
                  <td className="p-2">
                    <img
                      src={img.src_url}
                      alt={img.alt_text || 'Image'}
                      className="w-48 h-16 object-cover rounded border border-theme-border-subtle"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </td>
                  <td className="p-2 text-center font-mono">
                    {img.similarity_score?.toFixed(4) ?? 'N/A'}
                  </td>
                  <td className="p-2 text-center">
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${
                      img.passed_threshold ? 'bg-green-600' : 'bg-red-600'
                    } text-white`}>
                      {img.passed_threshold ? 'SELECTED' : 'FILTERED'}
                    </span>
                  </td>
                  <td className="p-2 text-xs">
                    {img.filter_reason || img.error || (img.passed_threshold ? 'Passed' : 'Below threshold')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

ImageDebugPanel.propTypes = {
  debugData: PropTypes.oneOfType([
    PropTypes.shape({
      type: PropTypes.string,
      model: PropTypes.string,
      selected_images: PropTypes.array,
      rejected_images: PropTypes.array,
    }),
    PropTypes.arrayOf(PropTypes.object),
  ]),
};

export default ImageDebugPanel;
